import { State } from '@app/store/state';

import {
  ContentType,
  DEFAULT_GENERATED_CONTENT_ENTRY,
  DEFAULT_TONE_OF_VOICE_ID,
  GeneratedContentEntry,
} from './domain';

export const selectContentComposerContentRepurposingState = (state: State) =>
  state.contentHub.contentComposer.contentRepurposing;

export const selectContentComposerToneOfVoiceConfig = (state: State) =>
  state.contentHub.contentComposer.toneOfVoiceConfig;

/**
 * Gets the selected tone of voice ID for Content Composer with fallback logic:
 * 1. If user has selected a tone (generationAttributes.toneOfVoiceId) → use it
 * 2. Else if contentRepurposing has a default tone (status === 'FINISHED' and value exists) → use it
 * 3. Else → use DEFAULT_TONE_OF_VOICE_ID
 */
export const selectContentComposerToneOfVoice = (state: State): string => {
  const selectedToneId =
    state.contentHub.contentComposer.generationAttributes.toneOfVoiceId;
  if (selectedToneId) {
    return selectedToneId;
  }

  const contentRepurposing =
    selectContentComposerContentRepurposingState(state);
  if (
    contentRepurposing.status === 'success' &&
    contentRepurposing.data &&
    contentRepurposing.data.eligibility === 'ELIGIBLE' &&
    contentRepurposing.data.toneOfVoice &&
    contentRepurposing.data.toneOfVoice.status === 'FINISHED' &&
    contentRepurposing.data.toneOfVoice.value
  ) {
    return contentRepurposing.data.toneOfVoice.value;
  }

  return DEFAULT_TONE_OF_VOICE_ID;
};

export const selectContentComposerContentHistory = (state: State) =>
  state.contentHub.contentComposer.contentHistory;

/**
 * Gets the latest content ID for a specific content type from history
 * Returns the first entry's ID if history exists, null otherwise
 */
export const selectContentComposerLatestContentId = (
  state: State,
  contentType: ContentType,
): string | null => {
  const history = selectContentComposerContentHistory(state);
  const typeHistory = history[contentType];
  if (
    typeHistory?.history &&
    typeHistory.history.length > 0 &&
    typeHistory.history[0]?.id
  ) {
    return typeHistory.history[0].id;
  }
  return null;
};

/**
 * Gets feedback for a specific content type, content ID, and option index
 */
export const selectContentComposerFeedback = (
  state: State,
  contentType: ContentType,
  contentId: string,
  optionIndex: number,
): 'thumbs_up' | 'thumbs_down' | null => {
  const feedback = state.contentHub.contentComposer.feedback[contentType];
  if (!feedback || !feedback[contentId]) {
    return null;
  }
  return feedback[contentId].options[optionIndex] ?? null;
};

/**
 * Gets generation attributes (shared across all content types)
 */
export const selectContentComposerGenerationAttributes = (state: State) =>
  state.contentHub.contentComposer.generationAttributes;

/**
 * Gets email audience configuration for Content Composer
 */
export const selectContentComposerEmailAudienceConfig = (state: State) =>
  state.contentHub.contentComposer.emailAudienceConfig;

/**
 * Gets the generated content cache
 */
export const selectContentComposerGeneratedContent = (state: State) =>
  state.contentHub.contentComposer.generatedContent;

/**
 * Gets a specific generated content entry by content ID.
 * Returns a default idle entry if not found in cache.
 */
export const selectGeneratedContentById = (
  state: State,
  contentId: string,
): GeneratedContentEntry => {
  const cache = selectContentComposerGeneratedContent(state);
  return cache[contentId] ?? DEFAULT_GENERATED_CONTENT_ENTRY;
};
/**
 * Gets whether a content generation API request is currently in progress
 */
export const selectContentComposerIsContentGenerationRequestInProgress = (
  state: State,
): boolean =>
  state.contentHub.contentComposer.isContentGenerationRequestInProgress;

/**
 * Determines if content generation is blocked for a specific content type.
 * Returns true if:
 * 1. An active API request is in progress (global block), OR
 * 2. The specified content type's latest history entry has status 'INITIAL' or 'IN_PROGRESS'
 *
 * This allows parallel generation across DIFFERENT content types (e.g., SOCIAL_POST + BLOG_POST)
 * while preventing concurrent generation within the SAME content type (which causes 409 conflicts).
 */
export const selectIsContentGenerationBlockedForType = (
  state: State,
  contentType: ContentType,
): boolean => {
  // Global check: API request in flight blocks all generation
  if (selectContentComposerIsContentGenerationRequestInProgress(state)) {
    return true;
  }

  // Per-type check: Only block if THIS content type has a processing item
  const history = selectContentComposerContentHistory(state);
  const typeHistory = history[contentType];
  if (
    typeHistory?.history &&
    typeHistory.history.length > 0 &&
    typeHistory.history[0]?.status
  ) {
    const latestStatus = typeHistory.history[0].status;
    if (latestStatus === 'INITIAL' || latestStatus === 'IN_PROGRESS') {
      return true;
    }
  }

  return false;
};

/**
 * Determines if content can be regenerated based on whether its attributes
 * still exist in the current configuration.
 *
 * Returns false if:
 * - Topic ID doesn't exist in current topics list
 * - Tone of voice ID doesn't exist in tone config (unless it's the default)
 * - For EMAIL: recipientTypeId doesn't exist in email audience config
 * - For EMAIL: emailTypeId doesn't exist in the recipient's types list
 *
 * This handles cases where topics have been regenerated and old topic IDs
 * are no longer valid.
 */
export const selectCanRegenerateContent = (
  state: State,
  topicId: string | undefined,
  toneOfVoiceId: string | undefined,
  contentType: ContentType,
  recipientTypeId?: string,
  emailTypeId?: string,
): boolean => {
  // Must have topic and tone
  if (!topicId || !toneOfVoiceId) {
    return false;
  }

  // Check if topic exists in current topics list
  const contentRepurposing =
    selectContentComposerContentRepurposingState(state);
  if (
    contentRepurposing.status !== 'success' ||
    !contentRepurposing.data ||
    contentRepurposing.data.eligibility !== 'ELIGIBLE' ||
    !contentRepurposing.data.topics.value
  ) {
    return false;
  }

  const topicExists = contentRepurposing.data.topics.value.some(
    t => t.id === topicId,
  );
  if (!topicExists) {
    return false;
  }

  // Check if tone exists in config (or is the default)
  const toneConfig = selectContentComposerToneOfVoiceConfig(state);
  if (toneOfVoiceId !== DEFAULT_TONE_OF_VOICE_ID) {
    if (
      toneConfig.status !== 'success' ||
      !toneConfig.data ||
      !toneConfig.data.some(t => t.key === toneOfVoiceId)
    ) {
      return false;
    }
  }

  // For EMAIL, validate recipient type and email type
  if (contentType === 'EMAIL') {
    if (!recipientTypeId || !emailTypeId) {
      return false;
    }

    const emailConfig = selectContentComposerEmailAudienceConfig(state);
    if (emailConfig.status !== 'success' || !emailConfig.data) {
      return false;
    }

    const audience = emailConfig.data.find(a => a.key === recipientTypeId);
    if (!audience) {
      return false;
    }

    const emailTypeExists = audience.types.some(t => t.key === emailTypeId);
    if (!emailTypeExists) {
      return false;
    }
  }

  return true;
};

export const selectContentComposerInitialized = (state: State) =>
  state.contentHub.contentComposer.isInitialized;
